import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Input } from '@/components';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import { useUser } from '@/context/userContext/UserContextProvider';
import { useNavigate } from 'react-router-dom';

const ProposalSchoolForm = React.forwardRef(
  ({ btsSchool, setBtsSchool, goToNextSection, proposalId }, ref) => {
    const [logoPreview, setLogoPreview] = useState(
      btsSchool.proposal.schoolLogoUrl || '',
    );
    const [imagePath, setImagePath] = useState(btsSchool.proposal.logo || '');
    const fileInputRef = useRef(null);
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const previewRef = useRef(null);

    const {
      createBtsSchoolProposal,
      updateBtsSchoolProposal,
      error: btsError,
      setError: setBtsError,
    } = useBts();

    const { getSalesReps, salesReps } = useUser();
    const navigate = useNavigate();

    const requiredFields = [
      'schoolName',
      'businessManager',
      'phoneNumber',
      'email',
      'booklistType',
      'salesRep',
    ];

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          [name]: value,
        },
      }));
    };

    const handleDropdownChange = (name, value) => {
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          [name]: value,
        },
      }));
    };

    const handleSalesRepSelect = (e) => {
      let selectedId;
      if (e && e.target) {
        selectedId = e.target.value;
      } else if (e && e.value !== undefined) {
        selectedId = e.value;
      }
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          salesRep: { id: selectedId ? parseInt(selectedId) : null },
        },
      }));
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/avif',
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file && !allowedTypes.includes(file.type)) {
        setFormError(
          'Invalid file type. Only .jpg, .jpeg, .png, and .avif files are allowed.',
        );
        fileInputRef.current.value = null;
      } else if (file && file.size > maxSize) {
        setFormError('File size should not exceed 5MB');
        fileInputRef.current.value = null;
      } else {
        setFormError('');
        setBtsSchool((prev) => ({
          ...prev,
          proposal: {
            ...prev.proposal,
            logoFile: file,
            schoolLogoUrl: file ? '' : prev.proposal.schoolLogoUrl, // Use schoolLogoUrl
          },
        }));
        setLogoPreview(file ? URL.createObjectURL(file) : '');
        setImagePath(file ? file.name : btsSchool.proposal.logo || '');
      }
    };

    // Clear field error on change or blur
    const handleFieldBlur = (e) => {
      const { name, value } = e.target;
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      setFormError('');
    };

    // Email validation helper
    const isValidEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    // Phone validation helper (10 digits, adjust as needed)
    const isValidPhone = (phone) => {
      return /^\d{10,}$/.test(phone.trim());
    };

    const validateForm = () => {
      let errors = {};
      for (let field of requiredFields) {
        let value = btsSchool.proposal[field];
        if (typeof value === 'string') value = value.trim();
        if (!value || value === '') {
          errors[field] = 'This field is required.';
        }
      }
      // Email validation
      if (btsSchool.proposal.email && !isValidEmail(btsSchool.proposal.email)) {
        errors.email = 'Enter a valid email address.';
      }
      // Phone validation
      if (
        btsSchool.proposal.phoneNumber &&
        !isValidPhone(btsSchool.proposal.phoneNumber)
      ) {
        errors.phoneNumber = 'Enter a valid phone number (min 10 digits).';
      }
      // SalesRep id check
      if (
        !btsSchool.proposal.salesRep ||
        !btsSchool.proposal.salesRep.id ||
        btsSchool.proposal.salesRep.id === 0
      ) {
        errors.salesRep = 'Select a sales rep.';
      }
      setFieldErrors(errors);
      setFormError(
        Object.keys(errors).length > 0 ? 'Please fix the errors above.' : '',
      );
      return Object.keys(errors).length === 0;
    };

    const handleNext = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      setFormError('');
      setFieldErrors({});
      setBtsError && setBtsError(null);

      try {
        let resp;
        if (proposalId) {
          // Update if id exists (from props)
          resp = await updateBtsSchoolProposal(btsSchool.proposal, proposalId);
          if (resp && resp.id) {
            goToNextSection(proposalId);
          }
          return;
        } else {
          // Create if no id
          resp = await createBtsSchoolProposal(btsSchool, e);
          if (resp && resp.id) {
            setBtsSchool((prev) => ({
              ...prev,
              proposal: {
                ...prev.proposal,
                ...resp,
              },
            }));
            goToNextSection(resp.id);
          } else {
            setFormError('Failed to create proposal. Please try again.');
          }
          return;
        }
      } catch (err) {
        setFormError(err?.message || 'An error occurred');
      }
    };

    // Fetch sales reps on mount
    useEffect(() => {
      getSalesReps();
      // eslint-disable-next-line
    }, []);

    const salesRepOptions = salesReps.map((rep) => ({
      value: rep.id,
      label: `${rep.salesRep} (${rep.firstName} ${rep.lastName})`,
      data: rep.id,
    }));

    // Modal close on outside click
    useEffect(() => {
      if (!showPreview) return;
      const handleClickOutside = (event) => {
        if (previewRef.current && !previewRef.current.contains(event.target)) {
          setShowPreview(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showPreview]);

    return (
      <>
        <form onSubmit={handleNext} ref={ref}>
          <div className="p-5 grid grid-cols-2 gap-4 ">
            <Input
              label="School Name"
              name="schoolName"
              placeholder="Enter school name"
              className="mb-4"
              value={btsSchool.proposal.schoolName}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              required
              error={fieldErrors.schoolName}
            />
            <Dropdown
              label="Sales Rep"
              name="salesRep"
              options={salesRepOptions}
              value={
                btsSchool.proposal.salesRep?.id !== undefined &&
                btsSchool.proposal.salesRep?.id !== null &&
                btsSchool.proposal.salesRep?.id !== 0
                  ? String(btsSchool.proposal.salesRep.id)
                  : ''
              }
              placeholder="Select Sales Rep"
              className="mb-4"
              onChange={handleSalesRepSelect}
              onBlur={handleFieldBlur}
              required
              error={fieldErrors.salesRep}
            />
            <Input
              label="Business Manager"
              name="businessManager"
              value={btsSchool.proposal.businessManager}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter business manager name"
              className="mb-4"
              required
              error={fieldErrors.businessManager}
            />
            <Input
              label="Phone Number"
              name="phoneNumber"
              value={btsSchool.proposal.phoneNumber}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter phone number"
              type="tel"
              className="mb-4"
              required
              error={fieldErrors.phoneNumber}
            />
            <Dropdown
              label="BookList Type"
              name="booklistType"
              value={btsSchool.proposal.booklistType}
              onChange={(e) =>
                handleDropdownChange('booklistType', e.value || e.target.value)
              }
              onBlur={handleFieldBlur}
              placeholder="Select Booklist Type"
              options={[
                { value: 'Booklist', label: 'Booklist' },
                { value: 'Bulk', label: 'Bulk' },
              ]}
              required
              error={fieldErrors.booklistType}
            />
            <Input
              label="Email"
              name="email"
              value={btsSchool.proposal.email}
              required
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              placeholder="Enter email"
              type="email"
              className="mb-4"
              error={fieldErrors.email}
            />

            <Input
              label="Created Date"
              name="createdDate"
              value={
                btsSchool.proposal.createdDate || new Date().toISOString()
                  ? new Date(
                      btsSchool.proposal.createdDate ||
                        new Date().toISOString(),
                    ).toLocaleDateString()
                  : ''
              }
              required
              placeholder="Enter created date"
              type="text"
              disabled
            />
            <div>
              <Input
                type="file"
                label="Logo"
                name="logo"
                placeholder="Enter logo"
                className="mb-4"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
              />
              <div className="mt-8">
                {(logoPreview || btsSchool.proposal.schoolLogoUrl) && (
                  <div className="col-span-2 flex items-center gap-2 mb-1">
                    {/* Show Preview button if preview or logoUrl */}
                    {(logoPreview || btsSchool.proposal.schoolLogoUrl) && (
                      <span
                        className="text-blue-500 cursor-pointer"
                        onClick={() => setShowPreview(true)}>
                        Preview Image
                      </span>
                    )}
                    {/* Show link only if schoolLogoUrl is present and no uploaded logo */}
                    {!logoPreview && btsSchool.proposal.schoolLogoUrl && (
                      <a
                        href={btsSchool.proposal.schoolLogoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs">
                        View Logo Link
                      </a>
                    )}
                    {showPreview && (
                      <div className="fixed inset-0 z-10 flex items-center justify-center bg-opacity-50 backdrop-blur-sm">
                        <div
                          ref={previewRef}
                          className="p-4 rounded-lg backdrop-blur-lg bg-white">
                          <img
                            src={
                              logoPreview || btsSchool.proposal.schoolLogoUrl
                            }
                            alt="School Logo"
                            className="max-h-[70vh] max-w-[90vw] object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {formError && (
              <div className="col-span-2 text-red-500 text-sm ml-2">
                {formError}
              </div>
            )}
            <div className="col-span-2 flex justify-end gap-2 mt-4">
              <Button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-3xl">
                Next
              </Button>
            </div>
          </div>
        </form>
      </>
    );
  },
);

export default ProposalSchoolForm;
